import React from 'react';
import cn from 'classnames';
import {
    Button,
    ButtonBase,
    Dialog,
    DialogActions,
    DialogContent,
    TextField,
    Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Conditional from '../Conditional';
import OverlayBox from '../OverlayBox';
import useLogic from './logic';
import { merge, pick } from 'lodash';
import DataGridDyl from '../DataGridDyl';
import Uploader from './Uploader';
import { Cached as RefreshIcon } from '@mui/icons-material';

const useStyles = makeStyles(theme => ({
    root: {},
    search: {
        marginBottom: theme.spacing(4)
    },
    table: {
        marginBottom: theme.spacing(2)
    },
    selectionText: {},
}));

/**
 * @template T
 * @typedef Props
 * @property {T[]} data
 * @property {string} value
 * @property {(id: string, object: T) => void} onChange
 * @property {(object: T) => string} getId
 * @property {(object: T) => string} getDisplayName
 * @property {import('@mui/x-data-grid').GridColumns} columns
 * @property {keyof T} [searchColumn]
 * @property {boolean} [isLoading]
 * @property {() => void} [refresh]
 * @property {boolean} [allowEmpty]
 * @property {boolean} [allowUpload]
 * @property {string} [uploadAccept]
 * @property {(file: File) => string} [onUploadConfirm]
 * @property {React.Component<{ file: File }>} [PreviewUploadComponent]
 * @property {{ text: string, onClick: () => void, icon: React.Component }[]} actions
 * @property {import('@mui/x-data-grid').DataGridProps} [DataGridProps]
 * @property {import('@mui/material').TextFieldProps} [FieldProps]
 * @property {import('@mui/material').DialogProps} [DialogProps]
 * @property {Partial<ReturnType<useStyles>>} [classes]
 * @property {JSX.Element | (props: { className: string, disabled: boolean, onClick: () => void}) => JSX.Element} [children]
 */

/**
 * @template T
 * @typedef {Props<T> & Omit<import('@mui/material').TextFieldProps, keyof Props<T>>} ObjectPickerProps
 */

/**
 * @template T
 * @param {ObjectPickerProps<T>} props
 */
function ObjectPicker(props) {
    const classes = useStyles(props);

    const {
        className: propsClass,
        data,
        getId,
        columns,
        disabled,
        isLoading,
        searchColumn,
        DataGridProps,
        DialogProps,
        refresh,
        allowUpload = false,
        uploadLabel,
        uploadAccept,
        PreviewUploadComponent,
        allowEmpty = false,
        actions = [],
        children,
    } = props;

    const FieldProps = merge({},
        props.FieldProps,
        pick(props, ['label', 'helperText', 'fullWidth', 'required']),
    );

    const {
        isOpen, setOpen,
        fieldText, onFieldClick,
        hasSelected, selectionText,
        selectionModel, onSelectionModelChange,
        onConfirm,
        onUploadConfirm,
    } = useLogic(props);

    return (
        <>
            {typeof children === 'function' ? (
                // For function children, invoke with props
                children({
                    className: cn(classes.root, propsClass),
                    onClick: onFieldClick,
                    disabled: disabled,
                })
            ) : !!children ? (
                // For any other children, render with ButtonBase wrapper
                <ButtonBase
                    className={cn(classes.root, propsClass)}
                    onClick={onFieldClick}
                    disabled={disabled}
                >
                    {children}
                </ButtonBase>
            ) : (
                // For no children, default to showing a 'field'
                <TextField
                    className={cn(classes.root, propsClass)}
                    variant='filled'
                    // For some reason, the wrapper's readOnly wasn't working
                    inputProps={{ readOnly: true }}
                    onClick={onFieldClick}
                    value={fieldText}
                    disabled={disabled}
                    {...FieldProps}
                />
            )}

            <Dialog
                open={isOpen}
                fullWidth
                maxWidth='lg'
                onClose={() => setOpen(false)}
                {...DialogProps}
            >
                <DialogContent>
                    <OverlayBox isLoading={isLoading}>
                        {/* Table */}
                        <DataGridDyl
                            className={classes.table}
                            columns={columns}
                            rows={data}
                            getRowId={getId}
                            selectionModel={selectionModel}
                            onSelectionModelChange={onSelectionModelChange}
                            hideFooterSelectedRowCount
                            searchField={searchColumn}
                            toolbarContent={(
                                <>
                                    {actions.map(({ text, onClick, icon: Icon }, index) => (
                                        <Button onClick={onClick} color='primary' key={index}>
                                            <Icon /> {text}
                                        </Button>
                                    ))}
                                    {!!refresh && (
                                        <Button onClick={refresh} color='primary'>
                                            <RefreshIcon/> Refresh
                                        </Button>
                                    )}
                                    {allowUpload && (
                                        <Uploader
                                            label={uploadLabel}
                                            accept={uploadAccept}
                                            onConfirm={onUploadConfirm}
                                            Preview={PreviewUploadComponent}
                                        />
                                    )}
                                </>
                            )}
                            {...DataGridProps}
                        />
                    </OverlayBox>

                    {/* Selection text */}
                    <Conditional if={hasSelected}>
                        <Typography variant='body1' className={classes.selectionText}>
                            Selected: {selectionText}

                            {allowEmpty && (
                                <Button onClick={() => onSelectionModelChange([])} color='primary' size='small'>
                                    Clear
                                </Button>
                            )}
                        </Typography>

                    </Conditional>
                </DialogContent>

                {/* Buttons */}
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant='contained'
                        color='primary'
                        onClick={onConfirm}
                        disabled={!hasSelected && !allowEmpty}
                    >
                        Select
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default ObjectPicker;